/**
 * Media bytes are bearer-authenticated (docs/CONTRACT.md §2/§4), so `<img src="/api/media/…">`
 * cannot work: every read goes through `fetchRaw` and becomes an object URL. Thumbnails, images
 * and audio are small enough to hold; video is fetched on demand by the player. The LRU cap below is
 * deliberately identical to apps/admin's (M13), so keep the two in step.
 */
import { api } from './api'

const objectUrls = new Map<string, string>()
const inflight = new Map<string, Promise<string>>()
const MAX_ENTRIES = 120

function remember(path: string, url: string): void {
  objectUrls.set(path, url)
  if (objectUrls.size <= MAX_ENTRIES) return
  const oldest = objectUrls.keys().next()
  if (oldest.done) return
  const stale = objectUrls.get(oldest.value)
  objectUrls.delete(oldest.value)
  if (stale) URL.revokeObjectURL(stale)
}

export async function loadMediaUrl(path: string): Promise<string> {
  const cached = objectUrls.get(path)
  if (cached) return cached
  const pending = inflight.get(path)
  if (pending) return pending
  const request = (async () => {
    const response = await api.fetchRaw(path)
    if (!response.ok) throw new Error(`media request failed with ${response.status}`)
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    remember(path, url)
    return url
  })()
  inflight.set(path, request)
  try {
    return await request
  } finally {
    inflight.delete(path)
  }
}

/** Saves a file message to disk without leaving the page (works in the Capacitor WebView). */
export async function downloadMedia(path: string, filename: string): Promise<void> {
  const url = await loadMediaUrl(path)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.rel = 'noopener'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}
